import {
  Beaker01Icon,
  GitBranch01Icon,
  PlayCircleIcon,
  Users03Icon,
} from '@langchain/untitled-ui-icons';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { SetupEvaluation } from '@/Pages/Onboarding/components/SetupEvaluation';
import { SetupObservability } from '@/Pages/Onboarding/components/SetupObservability';
import { Pane } from '@/components/Pane';
import { useSegmentEventWrapper } from '@/hooks/useSegmentEventWrapper';
import { useCurrentOrganization, useWorkspaceId } from '@/hooks/useSwr';
import { appOrganizationPath } from '@/utils/constants';

function GetStartedButton(
  props: {
    icon: React.ReactNode;
    label: string;
  } & (
    | {
        to: string;
      }
    | {
        panel: React.ReactNode;
      }
  )
) {
  const track = useSegmentEventWrapper(() => {
    return [
      'home_get_started_button_click',
      {
        button_name: props.label.toLowerCase().split(' ').join('_'),
      },
    ];
  });
  const handleClick = track(() => {
    if ('to' in props) {
      window.location.href = props.to;
    } else {
      setOpen(true);
    }
  });
  const [open, setOpen] = useState(false);
  return 'to' in props ? (
    <Link
      to={props.to}
      className="flex items-center gap-3 rounded-lg border border-secondary p-4 text-sm transition-colors hover:bg-secondary"
      onClick={handleClick}
    >
      <div className="rounded-full bg-brand-secondary p-2 text-brand-primary">
        {props.icon}
      </div>
      {props.label}
    </Link>
  ) : (
    <>
      <button
        type="button"
        className="flex items-center gap-3 rounded-lg border border-secondary p-4 text-sm transition-colors hover:bg-secondary"
        onClick={handleClick}
      >
        <div className="rounded-full bg-brand-secondary p-2 text-brand-primary">
          {props.icon}
        </div>
        {props.label}
      </button>
      <Pane title={props.label} open={open} onClose={() => setOpen(false)}>
        {props.panel}
      </Pane>
    </>
  );
}

export function HomeGetStarted() {
  const workspaceId = useWorkspaceId();
  const orgPrefix = `/${appOrganizationPath}/${workspaceId}`;
  const { data: currentOrganization } = useCurrentOrganization();

  const getStartedActions = [
    ...(!currentOrganization?.is_personal
      ? [
          {
            icon: <Users03Icon className="h-4 w-4" />,
            label: 'Invite your team',
            to: `${orgPrefix}/settings/members`,
          },
        ]
      : []),
    {
      icon: <GitBranch01Icon className="h-4 w-4" />,
      label: 'Set up tracing',
      panel: <SetupObservability className="h-full py-0" />,
    },
    {
      icon: <Beaker01Icon className="h-4 w-4" />,
      label: 'Run an evaluation',
      panel: <SetupEvaluation className="h-full py-0" />,
    },

    {
      icon: <PlayCircleIcon className="h-4 w-4" />,
      label: 'Try out playground',
      to: `${orgPrefix}/playground`,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <span className="text-lg font-medium capitalize">Get started</span>
      <div className="grid grid-cols-4 gap-4">
        {getStartedActions.map((action, index) => (
          <GetStartedButton
            key={index}
            icon={action.icon}
            label={action.label}
            {...(action.to ? { to: action.to } : { panel: action.panel })}
          />
        ))}
      </div>
    </div>
  );
}
